import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { telephonyApi, locationsApi } from '../../services/apiClient';

const TRANSPORT_OPTIONS = [
  { value: 'tls', label: 'TLS (Recommended)' },
  { value: 'udp', label: 'UDP (Fallback)' },
];

const CONTEXT_OPTIONS = [
  { value: 'caro-videophone', label: 'Videophone / Entrance Panel' },
  { value: 'caro-internal', label: 'Internal (Resident/Common Area)' },
  { value: 'caro-staff', label: 'Staff' },
  { value: 'caro-emergency', label: 'Emergency' },
];

/**
 * Provision SIP Device Dialog
 * Creates User + Person + SIP Endpoint for a location in one click.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {Object|null} location - Pre-selected location (null shows picker)
 * @param {Function} onSuccess - Called after successful provisioning
 */
export default function ProvisionSipDeviceDialog({ open, onClose, location, onSuccess }) {
  const [phase, setPhase] = useState('form'); // 'form' | 'loading' | 'success' | 'error'
  const [transport, setTransport] = useState('tls');
  const [context, setContext] = useState('caro-videophone');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  // Location picker state (when no location pre-selected)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const activeLocation = location || selectedLocation;

  // Fetch locations if no pre-selected location
  const fetchLocations = useCallback(async () => {
    if (location) return;
    setLoadingLocations(true);
    try {
      const { data } = await locationsApi.getAll();
      const list = data.locations || data.data || data || [];
      setLocations(Array.isArray(list) ? list : []);
    } catch {
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [location]);

  useEffect(() => {
    if (open && !location) {
      fetchLocations();
    }
  }, [open, location, fetchLocations]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase('form');
      setTransport('tls');
      setContext('caro-videophone');
      setResult(null);
      setError('');
      setCopied('');
      setSelectedLocation(null);
    }
  }, [open]);

  const handleProvision = async () => {
    if (!activeLocation) return;
    setPhase('loading');
    setError('');
    try {
      const { data } = await telephonyApi.provisionLocationSipDevice({
        locationId: activeLocation.id,
        transport,
        context,
      });
      setResult(data.data);
      setPhase('success');
      onSuccess?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to provision SIP device'
      );
      setPhase('error');
    }
  };

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleCopyAll = () => {
    if (!result) return;
    const text = [
      `Location: ${result.location.name}`,
      '',
      '--- Login Credentials ---',
      `Email: ${result.user.email}`,
      `Password: ${result.user.password}`,
      '',
      '--- SIP Configuration ---',
      `Server: ${result.sip.server}`,
      `Port: ${result.sip.port}`,
      `Transport: ${result.sip.transport.toUpperCase()}`,
      `Extension: ${result.sip.extension}`,
      `SIP Password: ${result.sip.password}`,
      `Realm: ${result.sip.realm}`,
    ].join('\n');
    navigator.clipboard.writeText(text);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const handleClose = () => {
    onClose();
  };

  const locationOptions = useMemo(() => {
    return locations.map((l) => ({
      id: l.id,
      name: l.friendlyName || l.name || `Location #${l.id}`,
    }));
  }, [locations]);

  // ─── Credential Row Helper ────────────────────────────────────
  const CredRow = ({ label, value, keyName, mono = true }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
      }}
    >
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 130, fontSize: '0.82rem' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', fontSize: '0.85rem' }}
        >
          {value}
        </Typography>
        <Tooltip title={copied === keyName ? 'Copied!' : 'Copy'}>
          <IconButton size="small" onClick={() => handleCopy(value, keyName)}>
            {copied === keyName ? (
              <SuccessIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <CopyIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhoneIcon color="primary" />
        Provision SIP Device
      </DialogTitle>

      <DialogContent>
        {/* ─── Form Phase ─── */}
        {phase === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {location ? (
              <Alert severity="info">
                Provisioning SIP device for <strong>{location.friendlyName || location.name}</strong>.
                This will create a login user and SIP endpoint.
              </Alert>
            ) : (
              <Autocomplete
                size="small"
                options={locationOptions}
                loading={loadingLocations}
                value={selectedLocation ? locationOptions.find((l) => l.id === selectedLocation.id) || null : null}
                onChange={(_, v) => setSelectedLocation(v ? { id: v.id, friendlyName: v.name } : null)}
                getOptionLabel={(o) => o.name}
                renderInput={(params) => (
                  <TextField {...params} label="Select Location" placeholder="Search locations..." required />
                )}
                isOptionEqualToValue={(o, v) => o.id === v.id}
              />
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Transport</InputLabel>
              <Select value={transport} label="Transport" onChange={(e) => setTransport(e.target.value)}>
                {TRANSPORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Context / Role</InputLabel>
              <Select value={context} label="Context / Role" onChange={(e) => setContext(e.target.value)}>
                {CONTEXT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ─── Loading Phase ─── */}
        {phase === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">
              Creating user, person, and SIP endpoint...
            </Typography>
          </Box>
        )}

        {/* ─── Error Phase ─── */}
        {phase === 'error' && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button variant="outlined" onClick={() => setPhase('form')}>Try Again</Button>
          </Box>
        )}

        {/* ─── Success Phase — Credentials ─── */}
        {phase === 'success' && result && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              SIP device provisioned for <strong>{result.location.name}</strong>
            </Alert>

            <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
              Save these credentials now. The login password cannot be retrieved later.
            </Alert>

            {/* Login Credentials */}
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              Login Credentials
            </Typography>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
              <CredRow label="Email" value={result.user.email} keyName="email" />
              <CredRow label="Password" value={result.user.password} keyName="userPwd" />
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* SIP Configuration */}
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              SIP Configuration
            </Typography>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
              <CredRow label="Server" value={result.sip.server} keyName="server" />
              <CredRow label="Port" value={String(result.sip.port)} keyName="port" />
              <CredRow label="Transport" value={result.sip.transport.toUpperCase()} keyName="transport" />
              <CredRow label="Extension" value={result.sip.extension} keyName="ext" />
              <CredRow label="SIP Password" value={result.sip.password} keyName="sipPwd" />
              <CredRow label="Realm" value={result.sip.realm} keyName="realm" />
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={copied === 'all' ? <SuccessIcon color="success" /> : <CopyIcon />}
              onClick={handleCopyAll}
            >
              {copied === 'all' ? 'Copied!' : 'Copy All Credentials'}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {phase === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'form' && (
          <Button
            variant="contained"
            onClick={handleProvision}
            disabled={!activeLocation}
          >
            Provision
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
